import { Separator, Text } from '@cuemath/web-comps';
import {
  Button,
  Checkbox,
  Menu,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import COLORS from '../../constants/colors';
import React from 'react';
import { CNA_DATA, getCNAData } from './utils';

const ModifyEcna = ({
  closeModal,
  saveEcna,
  cnaData,
  isEditable,
  studentName,
  region = '',
  timezone = '',
  grade,
}) => {
  const { grade_level: gradeLevel, key_need_l1: keyNeedL1, school_board: schoolBoard } =
    cnaData || {};
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState({
    grade_level: gradeLevel || '',
    key_need_l1: keyNeedL1 || [],
    school_board: schoolBoard || '',
  });

  const cnaDataToUse = React.useMemo(() => {
    const cnaRegion = timezone === 'Asia/Kolkata' ? 'INDIA' : region;
    return ['NAM', 'INDIA'].includes(cnaRegion) ? getCNAData(cnaRegion, grade) : CNA_DATA;
  }, [timezone, region, grade]);

  const lastPage = React.useMemo(() => Object.keys(cnaDataToUse).length, [cnaDataToUse]);

  const cnaDataLength = Object.keys(cnaDataToUse).length;

  const handleCardClick = (e, { name }) => {
    setData(prev => ({
      ...prev,
      grade_level: name,
    }));
  };

  const handleCheckbox = (e, { name, checked }) => {
    setData(prev => {
      const keyNeedL1 = checked
        ? [...prev.key_need_l1, name]
        : prev.key_need_l1.filter(item => item !== name);
      return { ...prev, key_need_l1: keyNeedL1 };
    });
  };

  const handleDropdownChange = (e, { name }) => {
    setData(prev => ({
      ...prev,
      school_board: name,
    }));
  };

  const handleNext = React.useCallback(() => {
    if (page === lastPage) {
      saveEcna(data);
      closeModal();

      return;
    }
    setPage(prev => prev + 1);
  }, [closeModal, data, lastPage, page, saveEcna]);

  const handleBack = React.useCallback(() => {
    setPage(prev => prev - 1);
  }, []);

  const arraysHaveSameMembers = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every(item => b.includes(item)) && b.every(item => a.includes(item));
  };

  const disabledNext = React.useMemo(() => {
    if (page === lastPage) {
      if (data.key_need_l1.length === 0) return true;

      // If NAM region and US course question exists, check if school_board is selected
      if (region === 'NAM' && cnaDataLength > 2 && !data.school_board) return true;

      return Object.entries(data).every(([key, value]) => {
        const ecnaValue = cnaData?.[key] ?? (Array.isArray(value) ? [] : '');
        if (Array.isArray(value) && Array.isArray(ecnaValue)) {
          return arraysHaveSameMembers(value, ecnaValue);
        }
        return value === ecnaValue;
      });
    }
    if (page === 1) {
      return !data.grade_level;
    }
    if (page === 3 && region === 'NAM') {
      return !data.school_board;
    }

    return false;
  }, [page, lastPage, data, cnaData, region, cnaDataLength]);

  return (
    <Modal open={true} onClose={closeModal}>
      <ModalHeader>
        <Text variant="h6">
          {isEditable ? 'Edit Customer Needs' : 'Customer Needs Assessment'}
        </Text>
        <Text variant="body3" color={COLORS.GRAY}>
          {isEditable
            ? `Update ${studentName}'s learning goals and current level.`
            : `Gather information about ${studentName}'s learning goals and current level.`}
        </Text>
        <Separator height={16} transparent />
        <Text variant="body1Medium" align="center">
          {cnaDataToUse[page].heading}
        </Text>
        <Separator height={4} transparent />
        <Text variant="body2" color={COLORS.GRAY} align="center">
          {cnaDataToUse[page].subHeading}
        </Text>
      </ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          {cnaDataToUse[page].type === 'card' && (
            <Menu fluid vertical>
              {cnaDataToUse[page].options.map(item => (
                <Menu.Item
                  key={item.value}
                  name={item.value}
                  onClick={handleCardClick}
                  active={data.grade_level === item.value}
                  style={{
                    border: data.grade_level === item.value ? '2px solid black' : 'none',
                    padding: 16,
                  }}
                >
                  <Text variant="h6">{item.heading}</Text>
                  <Text variant="body2" color={COLORS.GRAY}>
                    {item.value}
                  </Text>
                </Menu.Item>
              ))}
            </Menu>
          )}
          {cnaDataToUse[page].type === 'checkbox' && (
            <>
              {cnaDataToUse[page].options.map((item, index) => (
                <Segment key={index}>
                  <Checkbox
                    label={item}
                    checked={data.key_need_l1.includes(item)}
                    onChange={handleCheckbox}
                    name={item}
                  />
                </Segment>
              ))}
            </>
          )}
          {cnaDataToUse[page].type === 'dropdown' && (
            <Menu fluid vertical>
              {cnaDataToUse[page].options.map(item => (
                <Menu.Item
                  key={item.value}
                  name={item.value}
                  onClick={handleDropdownChange}
                  active={data.school_board === item.value}
                  style={{
                    border: data.school_board === item.value ? '2px solid black' : 'none',
                    padding: 16,
                  }}
                >
                  <Text variant="h6">{item.label}</Text>
                  <Text variant="body2" color={COLORS.GRAY}>
                    {item.value}
                  </Text>
                </Menu.Item>
              ))}
            </Menu>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {page > 1 && (
          <Button onClick={handleBack} basic>
            Previous
          </Button>
        )}
        <Button onClick={closeModal} basic>
          Cancel
        </Button>
        <Button onClick={handleNext} color="black" disabled={disabledNext}>
          {lastPage === page ? 'Save Customer Needs' : 'Next'}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default ModifyEcna;
